/* @flow */
import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { translate } from 'react-i18next';
import type { TranslatorProps } from 'react-i18next';
import RequireForAccess from 'react-access';
import fileDownload from 'js-file-download';
import Button from '@material-ui/core/Button';
import Snackbar from '@material-ui/core/Snackbar';

import { apiService } from 'services/ServiceRegistry';
import { DocumentList } from 'components/DocumentList/DocumentList';
import { updateDownloadAssetDocs, updateUploadAssetDocs } from 'permissions/asset';
import type { TypeDocumentListItem } from 'components/DocumentList/DocumentList';
import AssetStore from 'stores/AssetStore';
import UserStore from 'stores/UserStore';
import { ComponentWithInjectedProps } from 'utils/injectedProps';

import ContractStore from 'stores/ContractStore';
import styles from 'common-scss/DetailsPage.module.scss';

const deliveryNoteDetails: TypeDocumentListItem = {
  id: 1,
  icon: '#icon-user-checklist',
  name: 'Delivery Note Document',
  type: 'PDF',
  link: '/',
  create: true,
};

type DocumentProps = {
  title: String,
  documentType: String,
};

type InjectedProps = {
  userStore: UserStore,
  assetStore: AssetStore,
  contractStore: ContractStore,
} & TranslatorProps;

export type DocumentType = $Values<typeof DocumentTypes>;
export const DocumentTypes = Object.freeze({
  ASSET: 'asset',
  CONTRACT: 'contract',
  CONTRACT_COC: 'contract_coc',
});

@translate()
@inject('assetStore', 'contractStore', 'userStore')
@observer
export default class Documents extends ComponentWithInjectedProps<
  DocumentProps,
  {},
  InjectedProps
> {
  @observable localState = {
    formIsLoading: false,
    showResponseMessage: false,
    responseMessage: '',
  };
  acceptedFileTypes = [
    'image/jpg',
    'image/jpeg',
    'image/png',
    'image/gif',
    'text/csv',
    'application/pdf',
    'application/rtf',
    'application/doc',
    'application/txt',
    'application/xlx',
    'application/xls',
    'application/msword',
    'application/xhtml+xml',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  myUploadId = React.createRef();

  get uploadUrl() {
    const { documentType, assetStore, contractStore } = this.props;

    return documentType === DocumentTypes.ASSET
      ? `assets/${assetStore.asset.id}/store-file`
      : `contracts/${contractStore.contract.id}/documents/upload`;
  }

  get downloadAllUrl() {
    const { documentType, assetStore, contractStore } = this.props;

    if (documentType === DocumentTypes.ASSET) {
      return `assets/${assetStore.asset.id}/download-files`;
    }

    if (documentType === DocumentTypes.CONTRACT) {
      return `contracts/${contractStore.contract.id}/download-files`;
    }

    if (documentType === DocumentTypes.CONTRACT_COC) return false;
  }

  get hasDocuments() {
    const { documentType, assetStore, contractStore } = this.props;

    if (documentType === DocumentTypes.ASSET) {
      const calibrationCertificates = assetStore.getCalibrationCer;
      return calibrationCertificates.length > 0;
    }

    if (documentType === DocumentTypes.CONTRACT) {
      const documents = contractStore.documents;
      return documents.length > 0;
    }

    if (documentType === DocumentTypes.CONTRACT_COC) return true;
  }

  handleMyUploadId() {
    this.myUploadId.current.click();
  }

  handleInputChange = (selectorFiles: FileList, vField: Object) => {
    const { t, documentType, assetStore, contractStore } = this.props;

    // if file exist and type is accepted
    if (selectorFiles && this.acceptedFileTypes.indexOf(selectorFiles[0].type) > -1) {
      const vHeaders: Headers = new Headers();

      //vHeaders.set('Content-Type', 'application/json');
      //vHeaders.set('X-Requested-With', 'XMLHttpRequest');

      this.localState.responseMessage = 'Uploading...';
      this.localState.showResponseMessage = true;

      const vFormdata = new FormData();
      vFormdata.append('file', selectorFiles[0]);
      vFormdata.append('file_name', selectorFiles[0].name);

      apiService
        .createFileResource(this.uploadUrl, vFormdata, vHeaders)
        .then((response: Object) => {
          this.localState.responseMessage = t('general:messages.file_upload_success');
          this.localState.showResponseMessage = true;
          if (documentType === DocumentTypes.ASSET) {
            assetStore.loadAsset(assetStore.asset.id, false);
          }
          if (documentType === DocumentTypes.CONTRACT) {
            contractStore.loadDocuments(contractStore.contract.id);
          }
          if (documentType === DocumentTypes.CONTRACT_COC) {
            contractStore.loadCOCDocuments(contractStore.contract.id);
          }
          return response;
        })

        .catch((err: string) => {
          console.log(err);
          this.localState.responseMessage = t('general:messages.file_upload_error');
          this.localState.showResponseMessage = true;
        });
    } else {
      this.localState.responseMessage = t('general:messages.pdf_files_only');
      this.localState.showResponseMessage = true;
    }
  };

  zipDownloadAll = () => {
    const { t, documentType } = this.props;

    apiService
      .getZipResource(this.downloadAllUrl)
      .then((response: Object) => {
        this.localState.responseMessage = t('general:messages.file_download_success');
        this.localState.showResponseMessage = true;
        this.downloadZipFile(response, 'Certificates');
        return response;
      })

      .catch((err: string) => {
        this.localState.responseMessage = t('general:messages.file_download_error');
        this.localState.showResponseMessage = true;
      });
  };

  downloadZipFile(data: string, filename: string) {
    fileDownload(data, filename + '.zip', 'application/octet-stream');
  }

  renderAssetDocuments() {
    const { t, assetStore } = this.props;
    const asset = assetStore.asset;
    const deliveryNoteAssigned = Object.assign(deliveryNoteDetails, {
      link: '/assets/' + asset.id + '/delivery-note',
    });
    const calibrationCertificates = assetStore.getCalibrationCer;
    const historicalCertificates = assetStore.getHisCalibrationCer;

    return (
      <React.Fragment>
        {calibrationCertificates.length > 0 && (
          <DocumentList listItems={calibrationCertificates[0]} type={'calibration-certificate'} />
        )}
        {historicalCertificates.length > 0 && (
          <DocumentList
            collapsible={true}
            header={<span>{t('general:assets.historical_certificates')}</span>}
            headerIcon="#icon-archive"
            listItems={historicalCertificates}
          />
        )}

        {!asset.owned && (
          <React.Fragment>
            <h3>{t('general:contracts.delivery_note_header')}</h3>
            <DocumentList listItems={deliveryNoteAssigned} />
          </React.Fragment>
        )}
      </React.Fragment>
    );
  }

  renderContractDocuments() {
    const { contractStore } = this.props;

    return <DocumentList listItems={contractStore.getDocuments} />;
  }

  renderContractCOCDocuments() {
    const { contractStore, t } = this.props;

    return (
      <DocumentList
        collapsible={false}
        header={<span>{t('general:contracts.certificate_of_conformance')}</span>}
        headerIcon="#icon-contract"
        listItems={contractStore.getCOCDocuments}
      />
    );
  }

  render() {
    const {
      t,
      title,
      documentType,
      assetStore,
      userStore: { isReadOnly },
    } = this.props;
    const asset = assetStore.asset;

    return (
      <div className={styles['documents-tab']}>
        <div className={styles['flex-header']}>
          <div>
            <h3>{title}</h3>
            {documentType === DocumentTypes.ASSET &&
              (asset.owned ? null : (
                <p>{t('general:calibrations.messages.available_on_request')}</p>
              ))}
          </div>
          <RequireForAccess permissions={updateUploadAssetDocs}>
            <input
              type="file"
              ref={this.myUploadId}
              id="myUploadId"
              style={{ visibility: 'hidden', width: '100%' }}
              onChange={(e: *) => this.handleInputChange(e.target.files)}
            />
            {documentType !== DocumentTypes.CONTRACT_COC && (
              <Button
                variant="contained"
                color="primary"
                disabled={isReadOnly}
                onClick={() => this.handleMyUploadId()}
              >
                {t('general:actions.upload')}
              </Button>
            )}
          </RequireForAccess>
          <RequireForAccess permissions={updateDownloadAssetDocs}>
            {this.hasDocuments > 0 && this.downloadAllUrl && (
              <Button variant="contained" color="primary" onClick={(e: *) => this.zipDownloadAll()}>
                {t('general:actions.download_all')}
              </Button>
            )}
          </RequireForAccess>
        </div>

        {documentType === DocumentTypes.CONTRACT_COC && this.renderContractCOCDocuments()}
        {documentType === DocumentTypes.ASSET && this.renderAssetDocuments()}
        {documentType === DocumentTypes.CONTRACT && this.renderContractDocuments()}

        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={this.localState.showResponseMessage}
          onClose={() => {
            this.localState.showResponseMessage = false;
          }}
          ContentProps={{
            'aria-describedby': 'message-id',
          }}
          message={<span id="message-id">{this.localState.responseMessage}</span>}
        />
      </div>
    );
  }
}
